const en = {
  // ── Common ────────────────────────────────────────
  common: {
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    create: 'Create',
    update: 'Update',
    edit: 'Edit',
    close: 'Close',
    confirm: 'Confirm',
    search: 'Search',
    loading: 'Loading...',
    noData: 'No data',
    actions: 'Actions',
    status: 'Status',
    name: 'Name',
    description: 'Description',
    type: 'Type',
    all: 'All',
    active: 'Active',
    inactive: 'Inactive',
    online: 'Online',
    offline: 'Offline',
    idle: 'Idle',
    back: 'Back',
    logout: 'Logout',
    darkMode: 'Dark mode',
    lightMode: 'Light mode',
    notifications: 'Notifications',
    version: 'v2.1.0',
    language: 'Language',
    systemsOperational: 'Systems operational',
    liveDataConnected: 'Live data is connected',
    skipToContent: 'Skip to main content',
    openNavigation: 'Open navigation',
    closeNavigation: 'Close navigation',
  },

  // ── Auth ──────────────────────────────────────────
  auth: {
    title: 'OmniTrace',
    subtitle: 'Sign in to your account',
    email: 'Email',
    password: 'Password',
    signIn: 'Sign in',
    invalidCredentials: 'Invalid credentials',
    secureWorkspace: 'Secure workspace',
    liveIntelligence: 'LIVE FLEET INTELLIGENCE',
    heroLine1: 'Every vehicle.',
    heroLine2: 'One clear picture.',
    heroSubtitle: 'Turn live location, fleet health, driver safety, and operations into decisions your team can act on instantly.',
    realTimeInsight: 'Real-time insight',
    secureDesign: 'Secure by design',
    brandTagline: 'Fleet operations, beautifully connected.',
    protectedAccess: 'Protected with encrypted access and activity monitoring',
  },

  // ── Navigation ────────────────────────────────────
  nav: {
    myDevices: 'My Devices',
    dashboard: 'Dashboard',
    driver: 'Driver',
    identity: 'Identity',
    admin: 'Admin',
    users: 'Users',
    roles: 'Roles',
    devices: 'Devices',
    allDevices: 'All Devices',
    sensorDefinitions: 'Sensor Definitions',
    deviceAssignments: 'Device Assignments',
    digitalTwin: 'Digital Twin',
    ruleEngine: 'Rule Engine',
    rules: 'Rules',
    alerts: 'Alerts',
    liveMap: 'Live Map',
    tripPlayback: 'Trip Playback',
    geofences: 'Geofences',
    trips: 'Trips',
    dispatch: 'Dispatch',
    maintenance: 'Maintenance',
    remoteControl: 'Remote Control',
    analytics: 'Analytics',
    notifications: 'Notifications',
    sendNotification: 'Send Notification',
    trustCircle: 'Trust Circle',
    deviceGroups: 'Device Groups',
    billing: 'Billing',
    reports: 'Reports',
    security: 'Security',
    fuelAnalytics: 'Fuel Analytics',
    audiences: 'Audiences',
  },

  // ── Dashboard ─────────────────────────────────────
  dashboard: {
    title: 'Dashboard',
    overview: 'Overview as of',
    myAccount: 'My Account',
    devicesCount: 'Devices',
    onlineCount: '{{count}} online',
    usersCount: 'Users',
    inYourTenant: 'in your tenant',
    activeAlerts: 'Active Alerts',
    recentAlerts: 'Recent Alerts',
    recentNotifications: 'Recent Notifications',
    unreadNotifications: '{{count}} unread notifications',
    noAlerts: 'No recent alerts',
    noNotifications: 'No recent notifications',
    deviceStatus: 'Device Status',
    organization: 'Organization',
    tenantId: 'Tenant ID',
    plan: 'Plan',
    roles: 'Roles',
    visibleDevices: 'Visible Devices',
    online: 'Online',
    idle: 'Idle',
    parked: 'Parked',
    towing: 'Towing',
    offline: 'Offline',
    total: 'Total',
    leaderboard: 'Driver Leaderboard',
    topDrivers: 'Top Drivers',
    liveOperations: 'LIVE OPERATIONS',
    welcomeBack: 'Welcome back',
    commandSubtitle: 'Your fleet is connected. Here is what needs your attention today.',
    fleetAvailability: 'Fleet availability',
    moving: 'Moving',
    critical: 'Critical',
    liveMovement: 'Live movement detected',
    liveVehicleHealth: 'Live vehicle health and connection status',
    noTwinData: 'Live status will appear as soon as devices begin reporting.',
    trackedVehicle: 'Tracked vehicle',
    alertsSubtitle: 'Prioritized events from across your fleet',
    clear: 'All clear',
    quickActions: 'Quick actions',
    thisWeek: 'This week',
  },

  // ── Devices ───────────────────────────────────────
  device: {
    title: 'Devices',
    subtitle: 'Register and manage IoT devices',
    register: 'Register Device',
    editDevice: 'Edit Device',
    registerDevice: 'Register Device',
    identifier: 'IMEI / Serial',
    deviceType: 'Device Type',
    vehicleType: 'Vehicle Type',
    plateNumber: 'Plate Number',
    ownerName: 'Owner / Driver',
    ownerPlaceholder: 'Owner or driver name',
    phoneNumber: 'SIM Phone Number',
    timezone: 'Timezone',
    created: 'Created',
    info: 'Device Info',
    liveTwinState: 'Live Twin State',
    noLiveData: 'No live data yet',
    lastSeen: 'Last Seen',
    latitude: 'Latitude',
    longitude: 'Longitude',
    speed: 'Speed',
    heading: 'Heading',
    altitude: 'Altitude',
    health: 'Device health',
    updated: 'Updated',
    attachedSensors: 'Attached Sensors',
    noSensors: 'No sensors attached',
    fuelRecords: 'Fuel Records',
    noFuelRecords: 'No fuel records',
    theftSuspected: 'Theft suspected',
    cannotChangeId: 'Identifier cannot be changed after registration',
    idHelperText: 'Hardware unique ID or IMEI',
    confirmDelete: 'This will permanently delete the device and all its sensor data. This cannot be undone.',
    gpsTracker: 'GPS Tracker',
    vehicleObd: 'Vehicle OBD',
    assetTracker: 'Asset Tracker',
    personal: 'Personal',
    car: 'Car',
    truck: 'Truck',
    van: 'Van',
    motorcycle: 'Motorcycle',
    bus: 'Bus',
    heavyEquipment: 'Heavy Equipment',
    other: 'Other',
    none: 'None',
    tripIdleThreshold: 'Trip idle threshold (min)',
    tripIdleHelperText: 'Minutes of idle to end a trip (default 30)',
    failedToRegister: 'Failed to register device',
    notFound: 'Device not found',
    customTrackingSchedule: 'Custom tracking schedule (default: 24/7)',
    activeTrackingDays: 'Active tracking days:',
    startTime: 'Start time',
    endTime: 'End time',
    tripHistory: 'Trip History',
    noTrips: 'No trips recorded',
    serviceHistory: 'Service History',
    noServiceHistory: 'No service history',
  },

  // ── Device Groups ─────────────────────────────────
  deviceGroups: {
    title: 'Device Groups',
    subtitle: '{{count}} groups',
    create: 'New Group',
    noGroups: 'No device groups yet. Create one to organize your devices.',
    manageGroup: 'Manage',
    groupName: 'Group Name',
    color: 'Color',
    assignDevices: 'Add Devices',
    assignUsers: 'Assign to User',
    assignRoles: 'Assign to Role',
    selectRole: 'Select role to assign',
    confirmDelete: 'This will permanently delete the group. This cannot be undone.',
    noDescription: 'No description',
    deviceCount: '{{count}} devices',
    roleCount: '{{count}} roles',
  },

  // ── Audience Groups ────────────────────────────────
  audiences: {
    title: 'Audience Groups',
    subtitle: 'Notify groups of people when zone events happen',
    create: 'Create Audience',
    members: 'Members',
    addMember: 'Add member',
    memberName: 'Name',
    memberPhone: 'Phone number',
    platformUser: 'Platform user',
    externalContact: 'External contact',
    externalHint: 'External contacts will receive WhatsApp/SMS when multi-channel delivery ships',
    preset: 'Preset',
    presetGeneric: 'Generic',
    presetSchool: 'School transport',
    noGroups: 'No audience groups yet. Create one to start notifying people.',
  },

  // ── Device Assignments ────────────────────────────
  deviceAssignments: {
    title: 'Device Assignments',
    subtitle: 'Pick which devices each user can see · {{userCount}} users · {{deviceCount}} devices',
    selectUser: 'Select user to assign',
    devicesVisibleTo: 'Devices visible to {{name}}',
    noDevices: 'No devices in this tenant yet.',
    noDevicesRegistered: 'No devices yet. Register one in Devices → All Devices.',
    editAssignments: 'Edit assignments',
    searchPlaceholder: 'Search by name or email…',
    role: 'Role',
    allRoles: 'All roles',
    noUsersMatch: 'No users match the filter',
  },

  // ── Digital Twin ──────────────────────────────────
  twin: {
    title: 'Digital Twin',
    subtitle: 'Live device state — auto-refreshes every 10s',
    noDeviceTwinData: 'No device twin data yet. Ingest sensor or GPS data to see live device states here.',
    health: 'Health',
  },

  // ── Map ───────────────────────────────────────────
  map: {
    title: 'Live Map',
    trackedDevices: '{{count}} tracked devices',
    geofenceCount: '{{count}} geofences',
    searchVehicles: 'Search vehicles...',
    noVehicles: 'No vehicles found',
    fitAllDevices: 'Fit all devices',
    type: 'Type',
    status: 'Status',
    speed: 'Speed',
    heading: 'Heading',
    seen: 'Seen',
    location: 'Location',
    altitude: 'Altitude',
    owner: 'Owner / driver',
    vehicleType: 'Vehicle type',
    deviceType: 'Device type',
    phone: 'Phone',
    timezone: 'Timezone',
    lastSeen: 'Last seen',
    updated: 'Updated',
    deviceHealth: 'Device health',
    sensorData: 'Sensor data',
    follow: 'Follow',
    following: 'Following',
    openMaps: 'Open in maps',
    viewDetails: 'View full details',
    unknownDevice: 'Unknown device',
    fullscreen: 'Fullscreen',
    exitFullscreen: 'Exit fullscreen',
    online: 'online',
    idle: 'idle',
    parked: 'parked',
    towing: 'towing',
    offline: 'offline',
  },

  // ── Trip Playback ─────────────────────────────────
  playback: {
    title: 'Trip Playback',
    subtitle: 'Replay GPS paths — select multiple devices and a date range',
    selectSubtitle: 'Select devices and a date range, then click a trip to replay it',
    devices: 'Devices',
    selectDevices: 'Select devices...',
    from: 'From',
    to: 'To',
    points: '{{count}} points',
    play: 'Play',
    pause: 'Pause',
    restart: 'Restart',
    minStop: 'Min stop (min)',
    stops: '{{count}} stops',
    stopped: '{{duration}} stopped',
    noTripsInRange: 'No trips in range',
    noStopsDetected: 'No stops detected (min {{min}} min)',
    tripStart: 'Trip Start',
    tripEnd: 'Trip End',
    arrived: 'Arrived',
    departed: 'Departed',
    duration: 'Duration',
    location: 'Location',
    fromPrev: 'From prev',
    openGoogleMaps: 'Open in Google Maps',
    long: 'Long',
    medium: 'Medium',
    short: 'Short',
    fullscreen: 'Fullscreen',
    exitFullscreen: 'Exit fullscreen',
    longest: 'longest',
  },

  // ── Trips ─────────────────────────────────────────
  trips: {
    title: 'Trips',
    tripCount: '{{count}} trips in selected range',
    allDevices: 'All devices',
    noTrips: 'No trips in selected range. GPS data from tracked devices will generate trips automatically.',
    replay: 'Replay',
    completed: 'Completed',
    active: 'Active',
    details: 'Details',
    inProgress: 'in progress',
    start: 'Start',
    end: 'End',
    distance: 'Distance',
    duration: 'Duration',
    maxSpeed: 'Max Speed',
    avgSpeed: 'Avg Speed',
    gpsPoints: 'GPS Points',
    idleTime: 'Idle Time',
    time: 'Time',
    latitude: 'Latitude',
    longitude: 'Longitude',
    speedKmh: 'Speed (km/h)',
    heading: 'Heading',
    map: 'Map',
    view: 'View',
    noGpsPoints: 'No GPS points recorded for this trip',
    compare: 'Compare',
    comparison: 'Trip Comparison',
    selectTripsToCompare: 'Select 2-5 trips to compare',
  },

  // ── Geofences ─────────────────────────────────────
  geofences: {
    title: 'Geofences',
    zoneCount: '{{count}} zones — draw on map to create',
    drawGeofence: 'Draw Geofence',
    drawOnMap: 'Draw Geofence on Map',
    noGeofences: 'No geofences yet. Click "Draw Geofence" to create one on the map.',
    createGeofence: 'Create Geofence',
    shapeDrawn: '{{shape}} drawn',
    color: 'Color',
    radius: 'radius',
    pointCount: '{{count}} points',
    notifyDriver: 'Notify assigned driver on enter/exit',
    assignAudience: 'Notify audiences',
    audienceHint: 'These groups get a push notification when a device enters this zone',
  },

  // ── Analytics ─────────────────────────────────────
  analytics: {
    title: 'Analytics',
    subtitle: 'Historical sensor data and GPS track analysis',
    device: 'Device',
    sensorKey: 'Sensor Key',
    timeRange: 'Time Range',
    customRange: 'Custom range',
    last1h: 'Last 1h',
    last6h: 'Last 6h',
    last12h: 'Last 12h',
    last24h: 'Last 24h',
    last48h: 'Last 48h',
    last7d: 'Last 7 days',
    selectDevice: 'Select a device to begin exploring analytics.',
    speedChart: 'Speed Over Time',
    sensorChart: 'Sensor: {{key}}',
    gpsTrack: 'GPS Track',
  },

  // ── Rules & Alerts ────────────────────────────────
  rules: {
    title: 'Rules',
    createRule: 'Create Rule',
    editRule: 'Edit Rule',
    eventType: 'Event Type',
    conditions: 'Conditions (JSON)',
    ruleActions: 'Actions (JSON)',
    noRules: 'No rules yet',
    notifyDriver: 'Push notification to assigned driver',
  },
  alerts: {
    title: 'Alerts',
    severity: 'Severity',
    message: 'Message',
    acknowledge: 'Acknowledge',
    acknowledged: 'Acknowledged',
    filterSeverity: 'Filter by severity',
  },

  // ── Notifications ─────────────────────────────────
  notifications: {
    title: 'Notifications',
    markAllRead: 'Mark all read',
    filterUnread: 'Unread only',
    noNotifications: 'No notifications',
    deleteConfirm: 'Delete this notification?',
  },

  // ── Send Notifications ──────────────────────────
  sendNotification: {
    title: 'Send Notification',
    subtitle: 'Send push notifications to your tenant users',
    notificationTitle: 'Title',
    titlePlaceholder: 'Notification title',
    message: 'Message',
    messagePlaceholder: 'Write your notification message...',
    severity: 'Severity',
    info: 'Info',
    warning: 'Warning',
    critical: 'Critical',
    target: 'Recipients',
    allUsers: 'All Users',
    specificUsers: 'Specific Users',
    selectUsers: 'Select users',
    send: 'Send Notification',
    sending: 'Sending...',
    success: 'Notification sent to {{count}} users ({{push}} push delivered)',
    error: 'Failed to send notification',
    noUsers: 'No users selected',
  },

  // ── Notification Preferences ─────────────────────
  notifPrefs: {
    title: 'Notification Preferences',
    subtitle: 'Control how and when you receive alerts',
    channels: 'Notification Channels',
    push: 'Push Notifications',
    pushDesc: 'Receive push alerts on your device',
    email: 'Email Notifications',
    emailDesc: 'Receive critical alerts by email',
    inApp: 'In-App Notifications',
    inAppDesc: 'Show notifications inside the app',
    quietHours: 'Quiet Hours',
    quietHoursDesc: 'Suppress non-critical pushes during these hours. Critical alerts always come through.',
    quietStart: 'Start Time',
    quietEnd: 'End Time',
    throttle: 'Throttle',
    throttleDesc: 'Minimum interval between repeated push notifications for the same rule and device.',
    minInterval: 'Minimum interval',
    noThrottle: 'No throttle',
    throttle1m: '1 minute',
    throttle5m: '5 minutes',
    throttle15m: '15 minutes',
    throttle1h: '1 hour',
    save: 'Save Preferences',
    saved: 'Preferences saved',
    saveFailed: 'Failed to save preferences',
    loadError: 'Failed to load preferences',
  },

  // ── Fuel ──────────────────────────────────────────
  fuel: {
    title: 'Fuel Analytics',
    subtitle: 'Fuel consumption, costs, and theft detection',
    totalConsumed: 'Total Consumed',
    totalCost: 'Total Cost',
    costPerKm: 'Cost / km',
    refillCount: 'Refills',
    theftCount: 'Theft Events',
    avgConsumption: 'Avg L/100km',
    selectDevice: 'Select a device to view fuel analytics',
  },

  // ── Fleet ─────────────────────────────────────────
  fleet: {
    jobs: 'Dispatch',
    createJob: 'Create Job',
    maintenance: 'Maintenance',
    createSchedule: 'Create Schedule',
    commands: 'Remote Control',
    issueCommand: 'Issue Command',
    selectDevice: 'Select Device',
    commandType: 'Command',
  },

  // ── Commands ─────────────────────────────────────
  commands: {
    title: 'Remote Control',
    subtitle: 'Send commands to your tracked devices',
    totalCommands: 'Total Commands',
    lock: 'Lock Vehicle',
    unlock: 'Unlock Vehicle',
    engineDisable: 'Disable Engine',
    engineEnable: 'Enable Engine',
    reboot: 'Reboot Device',
    lockDesc: 'Remotely lock the vehicle doors',
    unlockDesc: 'Remotely unlock the vehicle doors',
    engineDisableDesc: 'Cut engine power — vehicle cannot start',
    engineEnableDesc: 'Restore engine power',
    rebootDesc: 'Restart the tracking device',
    confirmDangerous: 'This is a dangerous operation. The engine will be disabled and the vehicle cannot start. Are you sure?',
    queued: 'Queued',
    sent: 'Sent',
    acknowledged: 'Acknowledged',
    failed: 'Failed',
    issuedBy: 'Issued by',
    issuedAt: 'Issued',
    completedAt: 'Completed',
    noCommands: 'No commands issued yet',
    commandSent: 'Command sent successfully',
    commandFailed: 'Failed to send command',
  },

  // ── Maintenance ──────────────────────────────────
  maintenance: {
    title: 'Maintenance',
    schedulesConfig: 'Schedules & Config',
    serviceOrders: 'Service Orders',
    newSchedule: 'New Schedule',
    task: 'Task',
    everyKm: 'Every (km)',
    everyDays: 'Every (days)',
    noSchedules: 'No maintenance schedules yet',
    addCategory: 'Add Category',
    addType: 'Add Type',
    categoriesTypes: 'Categories & Types',
    newServiceOrder: 'New Service Order',
    orderTitle: 'Title',
    description: 'Description',
    priority: 'Priority',
    serviceType: 'Service Type',
    odometer: 'Odometer (km)',
    cost: 'Cost',
    vendor: 'Vendor',
    vendorContact: 'Vendor Contact',
    notes: 'Notes',
    status: 'Status',
    open: 'Open',
    inProgress: 'In Progress',
    completed: 'Completed',
    closed: 'Closed',
    reportIssue: 'Report Issue',
    noServiceOrders: 'No service orders yet',
    noIssues: 'No issues reported',
    issueTitle: 'Issue Title',
    issueSeverity: 'Severity',
    resolveIssue: 'Resolve',
    low: 'Low',
    normal: 'Normal',
    high: 'High',
    urgent: 'Urgent',
  },

  // ── Jobs ─────────────────────────────────────────
  jobs: {
    title: 'Dispatch',
    subtitle: 'Manage delivery jobs and driver assignments',
    customerName: 'Customer Name',
    customerPhone: 'Customer Phone',
    pickupAddress: 'Pickup Address',
    dropoffAddress: 'Dropoff Address',
    assignDriver: 'Assign Driver',
    assignVehicle: 'Assign Vehicle',
    scheduledAt: 'Scheduled At',
    priority: 'Priority',
    notes: 'Notes',
    noJobs: 'No jobs yet',
    pending: 'Pending',
    assigned: 'Assigned',
    accepted: 'Accepted',
    enRoute: 'En Route',
    inProgress: 'In Progress',
    completed: 'Completed',
    cancelled: 'Cancelled',
    reference: 'Reference',
  },

  // ── Billing ───────────────────────────────────────
  billing: {
    title: 'Billing & Subscription',
    subtitle: 'Manage your subscription plan, device count, and payment method',
    subscription: 'Expires',
    plan: 'Tier',
    expiresIn: 'Expires in {{days}} days',
    renew30: 'Renew',
    renew1y: 'Renew +1y',
    usageSummary: 'Total',
    paymentSuccess: 'Payment successful! Your subscription has been activated.',
    paymentFailed: 'Payment failed. Please try again.',
    currentSubscription: 'Current Subscription',
    pricingTiers: 'Pricing Tiers',
    configurePlan: 'Configure Plan',
    numberOfDevices: 'Number of Devices',
    billingCycle: 'Billing Cycle',
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    semiAnnual: 'Semi-Annual',
    annual: 'Annual',
    price: 'Per device',
    subscribe: 'Subscribe',
    upgrade: 'Upgrade',
    processing: 'Processing...',
    selectGateway: 'Payment Method',
    devices: 'registered',
    remainingDays: 'Remaining Days',
    noSubscription: 'No active subscription',
    planStatus: 'Plan Status',
    deviceUsage: 'Device Usage',
    devicesUsed: '{{used}} of {{limit}} devices used',
    startedAt: 'Started',
    expiresAt: 'Expires',
    daysLeft: '{{days}} days left',
    expired: 'Expired',
    walletBalance: 'Wallet Balance',
    recentTransactions: 'Recent Transactions',
    noTransactions: 'No transactions yet',
    topup: 'Top-up',
    deduction: 'Deduction',
    subscriptionPayment: 'Subscription',
  },

  // ── Trust Circle ──────────────────────────────────
  trustCircle: {
    title: 'Trust Circle',
    subtitle: 'Manage cross-tenant device access sharing',
    accessGranted: 'Access Granted',
    accessReceived: 'Access Received',
    grantAccess: 'Grant Access',
    revokeAccess: 'Revoke Access',
    revokeConfirm: 'Are you sure you want to revoke this access grant? The target tenant will immediately lose access.',
    noGrantedAccess: "You haven't granted access to any tenant yet.",
    noReceivedAccess: 'No tenants have granted you access.',
    targetTenant: 'Target Tenant ID',
    selectDevices: 'Select Devices',
    permissions: 'Permissions',
    expiresAt: 'Expires At',
    noGranted: 'No access granted to other tenants',
    noReceived: 'No access received from other tenants',
    accept: 'Accept',
    decline: 'Decline',
  },

  // ── Users ─────────────────────────────────────────
  users: {
    title: 'Users',
    subtitle: 'Manage users in your organization',
    addUser: 'New User',
    editUser: 'Edit User',
    assignRole: 'Assign Role',
    noRoles: 'no roles',
    confirmDelete: 'This will permanently delete the user. This cannot be undone.',
    email: 'Email',
    name: 'Name',
    password: 'Password',
    role: 'Roles',
    detail: 'User Info',
    profile: 'Profile',
    noUsers: 'No users yet',
    totalKm: 'Total km',
    totalTrips: 'Total Trips',
    totalDistance: 'Total Distance',
    assignedGroups: 'Assigned Groups',
    assignedDevices: 'Assigned Devices',
    noGroupsAssigned: 'No groups assigned',
    activity: 'Activity',
  },
  roles: {
    title: 'Roles & Permissions',
    subtitle: 'Define roles and assign permissions',
    createRole: 'New Role',
    editPermissions: 'Edit Permissions',
    deleteRole: 'Delete Role',
    deleteConfirm: 'Are you sure you want to delete this role? Users with this role will lose associated permissions.',
    roleName: 'Role Name',
    noRoles: 'No roles defined yet',
    noPermissions: 'No permissions assigned',
    addPermission: 'Add permission',
    confirmDelete: 'This will permanently delete the role.',
  },

  // ── Security ─────────────────────────────────────
  security: {
    title: 'Security Settings',
    subtitle: 'Two-factor authentication and trusted devices',
    twoFactorAuth: 'Two-Factor Authentication',
    twoFAEnabled: '2FA Enabled',
    twoFANotSetUp: '2FA Not Set Up',
    enforced: 'Enforced',
    twoFARequired: 'Two-factor authentication is required for your account. You will be prompted to set it up on your next login.',
    myTrustedDevices: 'My Trusted Devices',
    allTenantDevices: 'All Tenant Devices',
    deviceName: 'Device',
    ipAddress: 'IP Address',
    lastUsed: 'Last Used',
    expiresAt: 'Expires',
    revoke: 'Revoke',
    revoking: 'Revoking...',
    revokeConfirm: 'This will remove the device from your trusted list. You will need to enter a 2FA code on your next login from this device.',
    revokeAdminConfirm: "This will revoke the device trust and invalidate the user's session. They will be forced to log in again with 2FA.",
    noDevices: 'No trusted devices. When you log in with 2FA and check "Trust this device", it will appear here.',
    disable2FA: 'Disable 2FA',
    enforce2FA: 'Enforce 2FA',
    disable2FAConfirm: 'This will disable two-factor authentication on your account. You will need to set it up again if re-enabled. All trusted devices will be revoked. Enter your password to confirm.',
    wrongPassword: 'Incorrect password',
    disableFailed: 'Failed to disable 2FA',
    enterPassword: 'Enter your password',
    twoFADisabled: '2FA has been disabled for your account. You can log in with just your password.',
    changePassword: 'Change Password',
    changePasswordSubtitle: 'Update the password you use to sign in',
    currentPassword: 'Current password',
    newPassword: 'New password',
    confirmPassword: 'Confirm new password',
    passwordMismatch: 'New passwords do not match',
    passwordTooShort: 'Password must be at least 8 characters',
    passwordChanged: 'Password changed successfully',
    passwordChangeFailed: 'Failed to change password',
    wrongCurrentPassword: 'Current password is incorrect',
    save: 'Save',
  },

  // ── Two-Factor ────────────────────────────────────
  twoFactor: {
    title: 'Two-Factor Authentication',
    subtitle: 'Enter the 6-digit code from your authenticator app',
    enterCode: 'Verification Code',
    verify: 'Verify',
    backToLogin: 'Back to login',
    trustDevice: 'Trust this device for 30 days',
    setupTitle: 'Set Up Two-Factor Authentication',
    scanQR: 'Scan the QR code with Google Authenticator or any TOTP app',
    enterManually: 'Or enter this key manually:',
    secretKey: 'Secret Key',
    confirmCode: 'Verification Code',
    activate: 'Activate 2FA',
  },

  // ── Driver Dashboard ──────────────────────────────
  driver: {
    title: 'Welcome, {{name}}',
    defaultName: 'Driver',
    assignedDevices: 'ASSIGNED DEVICES',
    assignedDevicesSubtitle: 'Showing only devices assigned to you',
    onlineDevices: 'ONLINE',
    recentAlerts: 'OPEN ALERTS',
    noAlerts: 'No alerts',
    myDevices: 'My Devices',
    noDevicesAssigned: 'No devices assigned yet',
  },

  // ── Viewer Dashboard ──────────────────────────────
  viewer: {
    title: 'Viewer',
    assignedDevices: 'Read-only access · {{count}} device(s) visible',
    lastActivity: 'Last activity',
    never: 'never',
    noDevicesShared: 'No devices have been shared with you yet',
    contactAdmin: 'Ask your tenant administrator to grant access on the Device Assignments page.',
  },

  // ── Share Tracking ────────────────────────────────
  share: {
    title: 'Link Expired',
    loading: 'Loading tracking data...',
    invalidLink: 'This tracking link has expired or is no longer valid.',
    lastSeen: 'Updated',
    speed: 'Speed',
    expires: 'Expires',
    shareDevice: 'Share',
    createLink: 'Create Share Link',
    activeLinks: 'Share Links',
    noLinks: 'No active share links for this device',
    untitled: 'Untitled Link',
    copyLink: 'Copy link',
    revoke: 'Revoke link',
    copied: 'Link copied to clipboard',
    created: 'Share link created',
    failed: 'Failed to create share link',
    label: 'Link Label',
    expiresHours: 'Expires in (hours)',
    expiresHelp: '1–720 hours (default 24h)',
    expiresAt: 'Expires',
    destination: 'Destination (optional)',
    destLat: 'Latitude',
    destLng: 'Longitude',
    destLabel: 'Destination Name',
    generate: 'Generate Link',
  },

  // ── Ingestion ─────────────────────────────────────
  ingestion: {
    title: 'Data Ingestion',
    subtitle: 'Manually push sensor, location, and status data into the pipeline',
    sensorData: 'Sensor Data',
    gpsLocation: 'GPS Location',
    deviceStatus: 'Device Status',
  },

  // ── Reports ────────────────────────────────────
  reports: {
    title: 'Reports',
    subtitle: 'Generate, schedule, and download fleet reports',
    templates: 'Templates',
    schedules: 'Schedules',
    history: 'History',
    generateNow: 'Generate Report',
    generating: 'Generating...',
    generated: 'Report generated!',
    checkHistory: 'Check the History tab to download.',
    noTemplates: 'No report templates available',
    createSchedule: 'Create Schedule',
    reportType: 'Report Type',
    frequency: 'Frequency',
    dateRange: 'Date Range',
    filterDevices: 'Filter by Device',
    filterVehicleTypes: 'Filter by vehicle type',
    emailTo: 'Email recipients',
    emailHelperText: 'Comma-separated email addresses for report delivery',
    nextRun: 'Next Run',
    fileSize: 'Size',
    generatedAt: 'Generated',
    download: 'Download',
    customReportBuilder: 'Custom Report Builder',
    customReport: 'Custom',
    selectDataSource: 'Select a data source:',
    selectColumns: 'Select columns to include:',
    stepDataSource: 'Data Source',
    stepColumns: 'Columns',
    stepFilters: 'Filters',
    stepGrouping: 'Grouping',
    stepGenerate: 'Generate',
    groupingAndSorting: 'Grouping & Sorting:',
    groupBy: 'Group By',
    sortBy: 'Sort By',
    sortDefault: 'Default',
    sortOrder: 'Sort Order',
    ascending: 'Ascending',
    descending: 'Descending',
    reportNamePlaceholder: 'My Custom Report',
    lastDays: 'Last {{count}} day',
    devicesSelected: 'device(s) selected',
    reviewAndGenerate: 'Review & Generate:',
    sourceLabel: 'Source',
    columnsLabel: 'Columns',
    days: 'days',
    generateError: 'Failed to generate report. Please try again.',
    next: 'Next',
  },

  // ── Sensor Definitions ────────────────────────────
  sensors: {
    title: 'Sensor Definitions',
    subtitle: 'Define available sensor types and their metadata',
    createSensor: 'Create Sensor Definition',
    key: 'Key',
    keyLabel: 'Key (e.g. temperature)',
    keyHelperText: 'Snake_case identifier used in payloads',
    displayName: 'Display Name',
    unit: 'Unit',
    unitLabel: 'Unit (e.g. °C, km/h)',
    dataType: 'Data Type',
  },

  // ── Sensor Names (translations for seeded definitions) ──
  sensorNames: {
    battery_voltage: 'Battery Voltage',
    gsm_signal: 'GSM Signal',
    acc: 'ACC (Accelerometer)',
    charging: 'Charging',
    speed: 'Speed',
    fuel_level: 'Fuel Level',
    temperature: 'Temperature',
    humidity: 'Humidity',
    rpm: 'RPM',
    odometer: 'Odometer',
    engine_load: 'Engine Load',
    coolant_temp: 'Coolant Temperature',
    door_status: 'Door Status',
    ignition: 'Ignition',
    alarm_type: 'Alarm Type',
    external_voltage: 'External Voltage',
    fuel_consumption: 'Fuel Consumption',
    tire_pressure: 'Tire Pressure',
    altitude: 'Altitude',
    heading: 'Heading',
    oil_pressure: 'Oil Pressure',
    throttle_position: 'Throttle Position',
    transmission_temp: 'Transmission Temperature',
    brake_fluid_level: 'Brake Fluid Level',
    abs_status: 'ABS Status',
    airbag_status: 'Airbag Status',
    seatbelt_status: 'Seatbelt Status',
    harsh_braking: 'Harsh Braking',
    harsh_acceleration: 'Harsh Acceleration',
    sharp_turn: 'Sharp Turn',
    towing_detected: 'Towing Detected',
    crash_detected: 'Crash Detected',
    tamper_alert: 'Tamper Alert',
    jamming_detected: 'Jamming Detected',
  },
}

export default en
